import { Helmet } from 'react-helmet-async'
import { useTranslation, LANGUAGES } from '../context/LanguageContext'

type LocaleValue = string | Record<string, string>

interface SeoProps {
  title: LocaleValue
  description: LocaleValue
  canonicalPath?: string
}

const DEFAULT_PUBLIC_BASE_URL = 'https://chizhart.com'

const resolveLocaleValue = (value: LocaleValue, language: string) => {
  if (typeof value === 'string') return value
  return value[language] || value.en || Object.values(value)[0] || ''
}

const sanitizeOrigin = (value?: string) => (value || '').replace(/\/+$/, '')
const isLocalOrigin = (value: string) =>
  /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:\d+)?$/i.test(value)

const stripLangParam = (inputPath: string) => {
  if (!inputPath) return '/'
  const [rawPath, rawQuery = ''] = inputPath.split('?')
  const pathname = rawPath || '/'
  if (!rawQuery) return pathname
  const params = new URLSearchParams(rawQuery)
  params.delete('lang')
  const query = params.toString()
  return `${pathname}${query ? `?${query}` : ''}`
}

export function Seo({ title, description, canonicalPath = '' }: SeoProps) {
  const { language } = useTranslation()
  const resolvedTitle = resolveLocaleValue(title, language)
  const resolvedDescription = resolveLocaleValue(description, language)
  const currentPath = (() => {
    if (canonicalPath) return canonicalPath
    if (typeof window === 'undefined') return '/'
    return window.location.pathname || '/'
  })()
  const normalizedPath = stripLangParam(currentPath)
  const envOrigin = sanitizeOrigin(
    import.meta.env?.VITE_PUBLIC_BASE_URL || import.meta.env?.VITE_SITE_URL || DEFAULT_PUBLIC_BASE_URL,
  )
  const runtimeOrigin = typeof window !== 'undefined' ? sanitizeOrigin(window.location.origin) : ''
  const canonicalOrigin =
    (!isLocalOrigin(envOrigin) && envOrigin) || (!isLocalOrigin(runtimeOrigin) && runtimeOrigin) || ''
  const originForLinks = canonicalOrigin || runtimeOrigin
  const canonicalUrl = originForLinks ? `${originForLinks}${normalizedPath}` : normalizedPath

  const buildHrefForLanguage = (code: string) => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      if (code === 'en') {
        url.searchParams.delete('lang')
      } else {
        url.searchParams.set('lang', code)
      }
      const query = url.searchParams.toString()
      const base = originForLinks || ''
      return `${base}${url.pathname}${query ? `?${query}` : ''}`
    }
    const basePath = normalizedPath || '/'
    const [pathname, existingQuery = ''] = basePath.split('?')
    const params = new URLSearchParams(existingQuery)
    if (code === 'en') {
      params.delete('lang')
    } else {
      params.set('lang', code)
    }
    const query = params.toString()
    if (originForLinks) {
      return `${originForLinks}${pathname}${query ? `?${query}` : ''}`
    }
    return `${pathname}${query ? `?${query}` : ''}`
  }

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {LANGUAGES.map((lang) => {
        const href = buildHrefForLanguage(lang.code)
        const valueTitle = resolveLocaleValue(title, lang.code)
        return (
          <link
            key={lang.code}
            rel="alternate"
            hrefLang={lang.code}
            href={href}
            title={valueTitle}
          />
        )
      })}
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
    </Helmet>
  )
}
